import { useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  PrinterIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { DATE_DOT_FORMAT } from "../../../../constants";
import CancelLabelModal from "../../../../components/modals/CancelLabelModal";
import StatusModal from "../../../../components/modals/StatusModal";

type ShipmentLabel = {
  service: string;
  dimensions: string;
  weight: number;
  date: string;
  cost: number;
  status: "active" | "cancelling" | "cancelled";
};

type Shipment = {
  carrier: string;
  trackingNumber: string;
  label?: ShipmentLabel;
};

type ShipmentCardProps = {
  title: string;
  orderId: string;
  shipmentType: "customer" | "seller";
  shipment: Shipment;
};

export default function ShipmentCard({
  title,
  shipmentType,
  shipment,
}: ShipmentCardProps) {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRequestSubmittedModalOpen, setIsRequestSubmittedModalOpen] =
    useState(false);

  const label = shipment.label;
  const isLabelCancelling = label?.status === "cancelling";
  const isLabelCancelled = label?.status === "cancelled";
  const isLabelInactive = isLabelCancelling || isLabelCancelled;
  const formattedDate =
    label?.date && format(parseISO(label.date), DATE_DOT_FORMAT);

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-100 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 md:px-5 py-4.5">
        <h2 className="flex items-center gap-2 text-sm md:text-base font-medium text-gray-950">
          <TruckIcon className="size-5 text-blue-600" />
          {title}
        </h2>
      </div>

      <div className="flex flex-col px-4 md:px-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex size-14 md:size-16 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-[#F9FAFB] text-sm font-medium text-gray-950">
              {shipment.carrier}
            </div>
            <div className="flex min-w-0 flex-col gap-2 text-sm">
              <span className="text-gray-500">Tracking ID</span>
              {isLabelInactive ? (
                <span className="truncate text-gray-950 line-through">
                  {shipment.trackingNumber}
                </span>
              ) : (
                <span className="flex items-center gap-2 text-gray-950">
                  <span className="truncate">{shipment.trackingNumber}</span>
                  <button
                    type="button"
                    aria-label="Copy tracking ID"
                    className="text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <DocumentDuplicateIcon className="size-4" />
                  </button>
                </span>
              )}
            </div>
          </div>
          {isLabelInactive ? (
            <span
              className={clsx(
                "shrink-0 text-sm md:text-base",
                isLabelCancelling ? "text-orange-500" : "text-[#FF453A]",
              )}
            >
              {isLabelCancelling ? "Cancellation Pending" : "Label Cancelled"}
            </span>
          ) : (
            <button
              type="button"
              className="flex shrink-0 items-center justify-center gap-2 rounded-lg border border-blue-600 bg-white px-5 py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
            >
              Track on {shipment.carrier}
              <ArrowTopRightOnSquareIcon className="size-4" />
            </button>
          )}
        </div>

        {isLabelInactive && (
          <div className="mb-5 flex flex-col gap-2 rounded-xl bg-[#F9FAFB] p-4 text-sm md:text-base">
            <span className="text-gray-500">Status</span>
            <span className="flex items-center gap-2">
              {isLabelCancelled ? (
                <span className="flex items-center gap-1.5 text-green-500">
                  <CheckCircleIcon className="size-5" />
                  Refunded
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-orange-500">
                  <ClockIcon className="size-5" />
                  Cancellation in progress
                </span>
              )}
              <span className="text-gray-950">•</span>
              <span className="text-gray-950">${label.cost.toFixed(2)}</span>
            </span>
          </div>
        )}

        {label && (
          <Disclosure>
            {({ open }) => (
              <>
                <DisclosureButton className="-mx-4 md:-mx-5 flex items-center justify-center gap-1.5 border-t border-gray-100 bg-[#F9FAFB] py-3 text-sm text-gray-500 hover:text-gray-700 outline-none cursor-pointer">
                  {open ? "Hide Details" : "Show Details"}
                  <ChevronDownIcon
                    className={clsx(
                      "size-4 transition-transform duration-200",
                      open && "rotate-180",
                    )}
                  />
                </DisclosureButton>

                <DisclosurePanel
                  transition
                  className="-mx-4 md:-mx-5 grid grid-rows-[1fr] border-t border-gray-100 transition-all duration-200 ease-out data-closed:grid-rows-[0fr] data-closed:opacity-0"
                >
                  <div className="flex flex-col overflow-hidden px-4 md:px-5 pb-5">
                    <LabelRow label="Carrier">{shipment.carrier}</LabelRow>
                    <LabelRow label="Service">{label.service}</LabelRow>
                    <LabelRow label="Dimensions (in)">
                      {label.dimensions}
                    </LabelRow>
                    <LabelRow label="Weight">{label.weight} lbs</LabelRow>
                    <LabelRow label="Date">{formattedDate}</LabelRow>
                    <LabelRow label="Cost">${label.cost.toFixed(2)}</LabelRow>

                    {!isLabelInactive && (
                      <>
                        <div className="mt-5 flex gap-3 sm:gap-4">
                          <button
                            type="button"
                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 md:px-5 px-3 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors cursor-pointer"
                          >
                            <PrinterIcon className="size-5" />
                            Print Label
                          </button>
                          <button
                            type="button"
                            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#EFEFF1] md:px-5 px-3 py-2.5 text-sm font-medium text-gray-950 hover:bg-gray-200 transition-colors cursor-pointer"
                          >
                            <ArrowDownTrayIcon className="size-5" />
                            Download PDF
                          </button>
                        </div>

                        {shipmentType === "customer" && (
                          <button
                            type="button"
                            onClick={() => setIsCancelModalOpen(true)}
                            className="mt-4 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-[#FF453A] hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            Cancel Label
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </DisclosurePanel>
              </>
            )}
          </Disclosure>
        )}
      </div>

      {label && (
        <>
          <CancelLabelModal
            isOpen={isCancelModalOpen}
            carrier={shipment.carrier}
            trackingNumber={shipment.trackingNumber}
            cost={label.cost}
            onClose={closeCancelModal}
          />
          <StatusModal
            isOpen={isRequestSubmittedModalOpen}
            variant="success"
            title="Cancellation request submitted"
            message="Your request to cancel this shipping label has been submitted."
            onClose={() => setIsRequestSubmittedModalOpen(false)}
          />
        </>
      )}
    </section>
  );
}

const LabelRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-3.5 text-sm">
    <span className="shrink-0 text-gray-500">{label}</span>
    <span className="min-w-0 break-all text-right text-gray-950">
      {children}
    </span>
  </div>
);
